import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import LocalStorage from '@forwardflow/services/LocalStorage'
import { UserService } from '@forwardflow/services/UserService'
import { useAppStore } from '@forwardflow/store'
import {
   consumeHandle,
   identityPostUrl,
   rememberHandle,
} from '@forwardflow/services/AssistantConnect'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { ConnectDecision, decideConnect } from './memberships'

/**
 * `/connect/assistant` — hand this signed-in user's identity to the MCP server.
 *
 * Deliberately OUTSIDE `ProtectedRoutesLayout`: that layout runs an
 * organization-navigation effect on mount which would route the user to their
 * dashboard or onboarding and steal the handoff. This page does its own,
 * narrower check — is there a token — and nothing else.
 *
 * A FORM POST, not `fetch`. It is a cross-origin navigation, so it needs no
 * CORS grant, and the token travels in a body rather than a URL: never in a
 * `Referer`, a proxy log, or browser history.
 */
export default function ConnectAssistant() {
   const location = useLocation()
   const navigate = useNavigate()
   const currentOrganization = useAppStore((s) => s.currentOrganization)
   const formRef = useRef<HTMLFormElement>(null)
   const [handoff, setHandoff] = useState<{
      action: string
      handle: string
      token: string
   } | null>(null)
   const [error, setError] = useState<string | null>(null)
   /**
    * Plan 261 §A — a verified person with no workspace must not land on an
    * empty organization selector with nothing to do.
    *
    * `null` means the memberships have not been read yet or the person has
    * one, in which case this page behaves exactly as it always has. The other
    * two values stop and ask, because creating a workspace and accepting an
    * invitation are both things a person says yes to.
    */
   const [decision, setDecision] = useState<ConnectDecision | null>(null)
   const [pendingHandle, setPendingHandle] = useState('')
   const [creating, setCreating] = useState(false)
   const [email, setEmail] = useState('')

   useEffect(() => {
      void (async () => {
      const handle = consumeHandle(location.search)
      if (!handle) {
         // Absent or malformed. The user is trying to connect something, and an
         // ordinary sign-in is the right fallback — but we must not resume a
         // handoff they did not ask for, so nothing is carried forward.
         navigate('/signin', { replace: true })
         return
      }

      if (!LocalStorage?.get('access_token')) {
         // Keep it for the round trip — including through Google or Microsoft —
         // and let the ordinary login flow bring them back here.
         rememberHandle(handle)
         navigate(`/signin?${new URLSearchParams({ handle }).toString()}`, {
            replace: true,
         })
         return
      }

      /**
       * PROVE the token before handing it over, and let the ordinary refresh
       * run if it is stale.
       *
       * Reading `access_token` straight out of storage skipped the Axios
       * interceptor that refreshes on a 401 and writes the new token back. An
       * expired token then travelled to the MCP server, which asked
       * `/users/me` with it, got a 401, and could only report that the session
       * was not recognised — with nothing here to hint that a refresh was all
       * it needed.
       *
       * This call goes through that interceptor, so by the time it resolves
       * the stored token is valid.
       */
      try {
         await UserService.getUserData()
      } catch {
         rememberHandle(handle)
         navigate(`/signin?${new URLSearchParams({ handle }).toString()}`, {
            replace: true,
         })
         return
      }

      /*
       * Plan 261 §A — read the ACTUAL memberships before handing identity
       * over, not the organization this tab last selected.
       *
       * `getUserData` was already being called above to prove the token; its
       * answer carries the memberships, so this costs no extra request.
       */
      let verified: Awaited<ReturnType<typeof UserService.getUserData>> | null = null
      try {
         verified = await UserService.getUserData()
      } catch {
         // Proven a moment ago, so a failure here is transient. Carry on to
         // the existing path rather than blocking a person who has a workspace.
         verified = null
      }
      if (verified) {
         setEmail(String((verified as { email?: string }).email ?? ''))
         const next = decideConnect(verified as Parameters<typeof decideConnect>[0])
         if (next !== 'proceed') {
            setPendingHandle(handle)
            setDecision(next)
            return
         }
      }

      handOver(handle)
      })()
   }, [location.search, navigate])

   /** Everything from here down is unchanged for a person who has a workspace. */
   function handOver(handle: string) {
      const token = LocalStorage?.get('access_token')
      const action = identityPostUrl()
      if (!token || !action) {
         setError('This dashboard is not configured to connect assistants.')
         return
      }
      setHandoff({ action, handle, token: String(token) })
   }

   /**
    * Create the workspace, then RE-READ before continuing.
    *
    * `startOnboarding` is Forward Flow's existing workspace creation — the
    * same call the plan step makes — and no new business rule is introduced
    * here: this only moves when it happens, to before authorization rather
    * than after. The re-read matters because the consent that follows must be
    * granted against a membership that actually exists, never against one
    * this page assumed it had just made.
    */
   async function createWorkspace() {
      if (creating) return
      setCreating(true)
      setError(null)
      try {
         await OnboardingV2Service.startOnboarding({ email })
         const after = await UserService.getUserData()
         if (decideConnect(after as Parameters<typeof decideConnect>[0]) !== 'proceed') {
            setError(
               'Your workspace was created but is not ready yet. Reopen the link from your conversation in a moment.',
            )
            return
         }
         setDecision(null)
         handOver(pendingHandle)
      } catch {
         setError(
            'Your workspace could not be created. Try again, or start from the dashboard.',
         )
      } finally {
         setCreating(false)
      }
   }

   // Submitted from an effect rather than by the user: there is nothing here to
   // decide. The choosing happens on the MCP server's consent screen.
   useEffect(() => {
      if (handoff) formRef.current?.submit()
   }, [handoff])

   if (error) {
      return (
         <div className="flex items-center justify-center min-h-screen px-4">
            <p className="text-sm text-gray-400">{error}</p>
         </div>
      )
   }

   if (decision === 'invited') {
      // An invitation already exists. Accepting it is the right answer, and a
      // second workspace for the same person is not a fallback.
      return (
         <Ask title="You have an invitation waiting">
            <p className="text-sm text-gray-400">
               Someone has invited you to their Forward Flow workspace. Accept it
               first, then reopen the link from your conversation.
            </p>
            <button
               type="button"
               className="rounded bg-[#9f7bff] px-4 py-2 text-sm text-white"
               onClick={() => navigate('/initializing')}
            >
               Review my invitation
            </button>
         </Ask>
      )
   }

   if (decision === 'create') {
      return (
         <Ask title="Create your workspace">
            <p className="text-sm text-gray-400">
               You do not have a Forward Flow workspace yet. Creating one sets up
               your account so you can finish setup in your conversation. It does
               not choose a plan or charge anything.
            </p>
            <button
               type="button"
               disabled={creating}
               className="rounded bg-[#9f7bff] px-4 py-2 text-sm text-white disabled:opacity-50"
               onClick={() => void createWorkspace()}
            >
               {creating ? 'Creating…' : 'Create my workspace'}
            </button>
         </Ask>
      )
   }

   return (
      <div className="flex items-center justify-center min-h-screen px-4">
         <p className="text-sm text-gray-400">Connecting…</p>
         {handoff ? (
            <form ref={formRef} method="post" action={handoff.action} hidden>
               <input type="hidden" name="handle" value={handoff.handle} />
               <input type="hidden" name="token" value={handoff.token} />
               {/* A HINT ONLY. The MCP server re-reads the memberships from
                   /users/me and validates the choice against them. */}
               <input
                  type="hidden"
                  name="orgHint"
                  value={currentOrganization ?? ''}
               />
            </form>
         ) : null}
      </div>
   )
}

/** A stop-and-ask screen. Nothing here happens without the person saying so. */
const Ask = ({
   title,
   children,
}: {
   title: string
   children: React.ReactNode
}) => (
   <div className="flex min-h-screen items-center justify-center px-4">
      <div className="flex max-w-md flex-col gap-3 text-center">
         <h1 className="text-lg font-semibold text-white">{title}</h1>
         {children}
      </div>
   </div>
)
